import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { dataService } from '../../services/DataService';
import TitleBar from '../layout/PageTitle';

interface QuestionListProps {
  moduleId: string;
}

export default function QuestionList({ moduleId }: QuestionListProps) {
  const [questions, setQuestions] = useState<Array<Record<string, any>>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGerman, setShowGerman] = useState(true);
  const [showFrench, setShowFrench] = useState(true);
  const [showAnswers, setShowAnswers] = useState(false);
  const [showExplanations, setShowExplanations] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    dataService.queryItems('questions', `topic=${encodeURIComponent(moduleId)}`).then((res) => {
      if (res.statusCode === 200) {
        setQuestions(res.body);
      } else {
        setError('Fragen konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
      }
    }).catch(() => {
      setError('Fragen konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
    }).finally(() => {
      setIsLoading(false);
    });
  }, [moduleId]);

  function renderConditions(conditions: Array<Record<string, any>>) {
    const preparedConditions: Array<any> = [];
    if (conditions.length > 1) {
      preparedConditions.push('Bedingungen:');
      preparedConditions.push(<br />);
    } else if (conditions.length > 0) {
      preparedConditions.push('Bedingung:');
      preparedConditions.push(<br />);
    }
    conditions.forEach((condition: Record<string, any>) => {
      preparedConditions.push(`${condition.dependsOnQuestion} => ${condition.dependsOnAnswer}`);
      preparedConditions.push(<br />);
    });
    return preparedConditions;
  }

  function renderText(text: string | undefined, language: string) {
    return text || `(${language} fehlt)`;
  }

  if (isLoading) {
    return <div>Loading questions...</div>;
  }

  return (
    <>
      <TitleBar pageTitle="Alle Fragen" />
      <section className="section">
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        <Row>
          <Col>
            <div className="mb-3">
              <Link
                to="/admin/frage/"
                state={{ moduleId, activeTab: 'questions' }}
                className="btn btn-primary"
              >
                Neue Frage
              </Link>
            </div>
            <Form className="mb-3" aria-label="Sprachauswahl">
              <Form.Check
                inline
                id="show-german"
                label="Deutsch"
                type="checkbox"
                checked={showGerman}
                onChange={(event) => setShowGerman(event.target.checked)}
              />
              <Form.Check
                inline
                id="show-french"
                label="Französisch"
                type="checkbox"
                checked={showFrench}
                onChange={(event) => setShowFrench(event.target.checked)}
              />
            </Form>
            <Form className="mb-3" aria-label="Inhaltsauswahl">
              <Form.Check
                inline
                id="show-answers"
                label="Antworten"
                type="checkbox"
                checked={showAnswers}
                onChange={(event) => setShowAnswers(event.target.checked)}
              />
              <Form.Check
                inline
                id="show-explanations"
                label="Erklärungen"
                type="checkbox"
                checked={showExplanations}
                onChange={(event) => setShowExplanations(event.target.checked)}
              />
            </Form>
            <ul className="item-list no-list-style">
              {[...questions].sort((a, b) => Number(a.order) - Number(b.order)).map((q) => {
                const {
                  id, question, type, topic, order, conditions,
                } = q;
                const options = Array.isArray(q.options) ? q.options : [];
                return (
                  <li key={id} className="list-item">
                    <div className="list-item-title">
                      <Link
                        to={`/admin/frage/${id}`}
                        state={{ moduleId: topic, activeTab: 'questions' }}
                      >
                        {showGerman && <div>{`DE: ${renderText(question, 'Deutsch')}`}</div>}
                        {showFrench && <div>{`FR: ${renderText(q.question_fr, 'Französisch')}`}</div>}
                      </Link>
                    </div>
                    <div className="list-item-meta">
                      <span>
                        Modul:
                        {` ${topic}`}
                        <br />
                        Position:
                        {` ${order}`}
                        <br />
                        Typ:
                        {` ${type}`}
                        <br />
                        ID:
                        {` ${id}`}
                        <br />
                        {(conditions)
                          ? renderConditions(conditions)
                          : null}
                      </span>
                    </div>
                    {(showAnswers || showExplanations) && (
                      <div className="mt-2">
                        <strong>{showAnswers ? 'Antworten' : 'Erklärungen'}</strong>
                        {options.length === 0 ? (
                          <div className="text-muted">Keine Antwortoptionen vorhanden.</div>
                        ) : (
                          <ul className="mb-0">
                            {options.map((option: Record<string, any>, index: number) => {
                              const metadata = [
                                option.correct !== undefined && `Korrekt: ${option.correct}`,
                                option.risk !== undefined && `Risiko: ${option.risk}`,
                                option.position !== undefined && `Position: ${option.position}`,
                                option.inputType !== undefined && `Input-Typ: ${option.inputType}`,
                              ].filter(Boolean).join(' | ');
                              return (
                                <li key={option.id || index}>
                                  {showAnswers && (
                                    <>
                                      <span>{`ID: ${option.id || 'fehlt'}`}</span>
                                      {showGerman && <div>{`DE: ${renderText(option.text, 'Deutsch')}`}</div>}
                                      {showFrench && <div>{`FR: ${renderText(option.text_fr, 'Französisch')}`}</div>}
                                      {metadata && <div>{metadata}</div>}
                                    </>
                                  )}
                                  {showExplanations && (
                                    <div className={showAnswers ? 'mt-2' : ''}>
                                      <strong>Erklärung</strong>
                                      {showGerman && <div>{`DE: ${renderText(option.feedback, 'Deutsch')}`}</div>}
                                      {showFrench && <div>{`FR: ${renderText(option.feedback_fr, 'Französisch')}`}</div>}
                                    </div>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </Col>
        </Row>
      </section>
    </>
  );
}
